import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Scissors, Check, SlidersHorizontal } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';
import { formatDay, formatShortDay } from '../../../lib/overOrder';

/**
 * Three ways out of a hold, and only three: approve the order as the client
 * asked for it, trim every over-cap line to its cap, or set the quantities by
 * hand. An approval can only ever reduce a line.
 */
const MODES = [
  { key: 'trim', label: 'Trim to the cap', icon: Scissors, hint: 'Every line above its ceiling drops to the ceiling.' },
  { key: 'approve', label: 'Approve as ordered', icon: Check, hint: 'The client gets exactly what they asked for.' },
  { key: 'reduce', label: 'Set by hand', icon: SlidersHorizontal, hint: 'Choose each quantity yourself.' },
];

export const ApprovalDialog = ({ orderId, open, onOpenChange, onResolved }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('trim');
  const [note, setNote] = useState('');
  const [manual, setManual] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !orderId) { setDetail(null); return; }
    setLoading(true);
    setMode('trim');
    setNote('');
    (async () => {
      try {
        const data = await adminService.getOrderApproval(orderId);
        setDetail(data);
        const seed = {};
        (data.lines || []).forEach((l) => { seed[l.product_id] = l.cap_qty; });
        setManual(seed);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load this order');
        onOpenChange?.(false);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderId]);

  const lines = useMemo(() => detail?.lines || [], [detail]);
  const trimSaving = useMemo(
    () => lines.reduce((sum, l) => sum + (l.ordered_qty - l.cap_qty), 0),
    [lines],
  );

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = { mode, note: note || null, lines: [] };
      if (mode === 'reduce') {
        payload.lines = Object.entries(manual).map(([product_id, quantity]) => ({
          product_id, quantity: Number(quantity) || 0,
        }));
      }
      const res = await adminService.approveOverOrder(orderId, payload);
      toast.success(res.message || 'Order released');
      onResolved?.(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to release this order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="approval-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            Review {detail?.order_number || 'order'}
            {detail?.is_held && <Badge className="bg-amber-100 text-amber-800">Held</Badge>}
          </DialogTitle>
          <DialogDescription>
            {detail?.client_name} · delivery {formatShortDay(detail?.delivery_date) || '—'} · decide by{' '}
            {formatShortDay(detail?.approval_deadline) || 'the ordering cutoff'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
          </div>
        ) : !detail ? null : (
          <div className="space-y-5">
            {detail.reason_label && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-xs text-slate-500">Why the client kept these quantities</p>
                <p className="text-sm font-medium text-slate-900">{detail.reason_label}</p>
                {detail.reason_note && (
                  <p className="text-sm text-slate-700 mt-0.5">“{detail.reason_note}”</p>
                )}
              </div>
            )}

            {lines.length === 0 ? (
              <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-3">
                <p className="text-sm text-green-900 font-medium">Nothing is above the cap any more</p>
                <p className="text-xs text-green-800/80 mt-0.5">
                  The order has been edited, or the client's own figures have moved. Approving it
                  now sends it through unchanged.
                </p>
              </div>
            ) : (
              <div className="rounded-lg border divide-y">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase">
                  <div className="col-span-5">Product</div>
                  <div className="col-span-2 text-right">Ordered</div>
                  <div className="col-span-2 text-right">Best day</div>
                  <div className="col-span-3 text-right">{mode === 'reduce' ? 'New qty' : 'Cap'}</div>
                </div>
                {lines.map((line) => (
                  <div
                    key={line.product_id}
                    className="grid grid-cols-12 gap-2 px-3 py-2 items-center"
                    data-testid={`approval-line-${line.product_id}`}
                  >
                    <div className="col-span-5 min-w-0">
                      <p className="text-sm font-medium truncate">{line.product_name}</p>
                      <p className="text-xs text-gray-500">
                        {line.deliveries} deliveries
                        {line.sell_through_pct != null ? ` · ${line.sell_through_pct}% sold` : ''}
                        {line.suggested_qty != null ? ` · suggested ${line.suggested_qty}` : ''}
                      </p>
                    </div>
                    <div className="col-span-2 text-right text-sm font-semibold text-orange-700 tabular-nums">
                      {line.ordered_qty}
                    </div>
                    <div className="col-span-2 text-right text-sm text-gray-700 tabular-nums">
                      {line.peak_sold}
                      {line.peak_date && (
                        <span className="block text-xs text-gray-400">{formatDay(line.peak_date)}</span>
                      )}
                    </div>
                    <div className="col-span-3 text-right">
                      {mode === 'reduce' ? (
                        <Input
                          type="number"
                          min={1}
                          max={line.ordered_qty}
                          value={manual[line.product_id] ?? line.cap_qty}
                          onChange={(e) => setManual((m) => ({ ...m, [line.product_id]: e.target.value }))}
                          className="h-8 w-20 ml-auto text-right text-sm"
                          data-testid={`approval-manual-${line.product_id}`}
                        />
                      ) : (
                        <span className="text-sm font-medium tabular-nums">{line.cap_qty}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div>
              <Label className="text-sm font-semibold text-gray-900">What happens to this order</Label>
              <div className="mt-2 grid gap-2 sm:grid-cols-3">
                {MODES.map((m) => {
                  const Icon = m.icon;
                  const selected = mode === m.key;
                  return (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => setMode(m.key)}
                      className={`text-left rounded-lg border px-3 py-2 transition-colors ${
                        selected
                          ? 'border-orange-400 bg-orange-50 ring-1 ring-orange-200'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                      data-testid={`approval-mode-${m.key}`}
                    >
                      <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                        <Icon className="h-4 w-4 text-orange-600" />
                        {m.label}
                      </span>
                      <span className="block text-xs text-gray-500 mt-0.5">{m.hint}</span>
                    </button>
                  );
                })}
              </div>
              {mode === 'trim' && trimSaving > 0 && (
                <p className="text-xs text-gray-600 mt-2" data-testid="approval-trim-summary">
                  Trimming removes {trimSaving} unit{trimSaving === 1 ? '' : 's'} from the order.
                </p>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-700">Note (optional)</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Kept in full — they have the festival on."
                className="mt-1 text-sm"
                data-testid="approval-note-input"
              />
            </div>

            <p className="text-xs text-gray-500">
              Releasing the order confirms it and emails the client what changed. An approval can
              reduce a line, never increase it.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="approval-cancel-btn">
            Cancel
          </Button>
          <Button
            className="bg-orange-600 hover:bg-orange-700"
            onClick={handleSubmit}
            disabled={saving || loading || !detail?.is_held}
            data-testid="approval-submit-btn"
          >
            {saving ? 'Releasing...' : 'Release order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApprovalDialog;
